<template>
  <div id="app">
    <transition name="fade" mode="out-in">
      <LoadingOverlay
        v-if="!store.sectionsLoaded || !store.webGLLoaded"
        class="app__loading-overlay"
      />
    </transition>

    <transition name="fade" mode="out-in">
      <NetworkStatus />
    </transition>

    <WebGL v-if="webGLAppIsSupported" class="web-gl" />

    <ViewContainer v-if="store.sectionsLoaded && store.webGLLoaded" #default="viewProps">
      <router-view class="view" v-bind="viewProps" />
    </ViewContainer>
  </div>
</template>

<script>
import Vue from 'vue';
import Vue2TouchEvents from 'vue2-touch-events';
import LoadingOverlay from './components/loadingOverlay/LoadingOverlay';
import NetworkStatus from './components/statusScreens/NetworkStatus';
import WebGL from './components/webGL/WebGL';
import ViewContainer from './components/ViewContainer';

import {
  initSoundController,
  destroySoundController,
  playSound,
  setStartPlayback,
} from './services/sound';

import { players, sounds } from './services/sound/config';

import store from './store';

import {
  disableEdgeSwipeNavigation,
  enableEdgeSwipeNavigation,
  disablePinchToZoom,
  enablePinchToZoom,
  supportsWebGL,
} from './utils/helpers';

import router from './router';

Vue.use(Vue2TouchEvents);

export default {
  name: 'App',

  components: {
    LoadingOverlay,
    NetworkStatus,
    ViewContainer,
    WebGL,
  },

  data() {
    return {
      webGLAppIsSupported: supportsWebGL(),
      deviceIsOnline: true,
      store,
      soundController: {},
    };
  },

  computed: {
    webGLLoaded: () => store.webGLLoaded,
    soundEnabled() {
      return this.soundController?.soundEnabled;
    },
    startPlayback() {
      return this.soundController?.startPlayback;
    },
  },

  watch: {
    webGLLoaded: {
      immediate: true,
      handler(webGLLoaded) {
        if (!webGLLoaded || Object.keys(this.soundController).length > 0) return;
        this.soundController = initSoundController(players, sounds);
        this.$once('hook:beforeDestroy', destroySoundController);
      },
    },
  },

  created() {
    if (!this.webGLAppIsSupported) {
      router.push({ name: 'upgrade' });
    }

    disableEdgeSwipeNavigation();
    this.$once('hook:beforeDestroy', enableEdgeSwipeNavigation);

    disablePinchToZoom();
    this.$once('hook:beforeDestroy', enablePinchToZoom);

    this.$watch(
      () => ({
        soundController: this.soundController,
        soundEnabled: this.soundEnabled,
        startPlayback: this.startPlayback,
      }),
      (conditions, oldConditions) => {
        if (conditions.soundEnabled && oldConditions.soundEnabled === false) {
          setStartPlayback(true);
        }
        if (Object.values(conditions).every(Boolean)) {
          playSound('background', 'music', { continue: true });
        }
      }
    );
  },

  head() {
    const { locale } = this.$i18n;
    return {
      htmlAttrs: {
        lang: locale,
        'xml:lang': locale,
        xmlns: 'http://www.w3.org/1999/xhtml',
      },

      // all titles will be injected into this template
      // titleTemplate: this.$t('meta.appTitleTemplate', locale, {
      //   appTitle: '%s', // <- the title template key of vue-meta
      // }),

      // if no subcomponents specify a metaInfo.title, this title will be used
      title: this.$t('meta.shareTitle'),

      meta: [
        { name: 'description', content: this.$t('meta.siteDescriptionShort') },
        { rel: 'canonical', href: this.$t('meta.urlCanonical') },

        // Open Graph
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: this.$t('meta.urlShort') },
        { property: 'og:title', content: this.$t('meta.shareTitle') },
        { property: 'og:description', content: this.$t('meta.shareDescription') },
        { property: 'og:image', content: process.env.VUE_APP_SHARE_IMAGE }, // TODO: dynamic import

        // Twitter Cards extra
        { property: 'twitter:title', content: this.$t('meta.shareTitle') },
        { property: 'twitter:description', content: this.$t('meta.shareDescription') },
        { property: 'twitter:img', content: process.env.VUE_APP_SHARE_IMAGE },
        { property: 'twitter:card', content: 'summary_large_image' },
        { property: 'twitter:site', content: this.$t('meta.shareTwitterHandle') },

        {
          property: 'keywords',
          content:
            'sustainability management, responsible business, carbon footprint, sustainability challenge, sustainability gamification, sustainable development goals, sdg, tipple bottom line, balance, expo',
        },
      ],
    };
  },
};
</script>

<style lang="scss">
@import 'normalize-scss';
@include normalize();
@import '@/styles/sapOverwrites.scss';

html,
body,
#app {
  // https://stackoverflow.com/questions/50013434/safari-11-3-1-status-bar-turned-white-on-white#comment102861264_50496324
  // height: calc(100% + env(safe-area-inset-top) + env(safe-area-inset-bottom));
  height: 100vh;
}

$scrollbar-color: $color-white;
$scrollbar-background: transparent;
body {
  scrollbar-color: $scrollbar-color $scrollbar-background;
  touch-action: pan-y;
}

::-webkit-scrollbar-track-piece {
  background-color: $scrollbar-color;
}

::-webkit-scrollbar-thumb:vertical {
  background-color: $scrollbar-background;
}

html {
  background-color: $color-background;
}

body {
  @include font-base;
  font-size: 62.5%;
  font-weight: normal;
  // fix overlay on iOS fullscreen
  background-color: transparent;
  color: $color-white;

  // prevent pull to refresh on Android
  overscroll-behavior: none;
}

p,
input,
button {
  margin: 0;
}

a {
  text-decoration: none;
}

ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

b,
strong,
h1,
h2,
h3,
h4,
h5,
h6 {
  font-weight: bold;
}

h1,
h2,
h3,
h4,
h5,
h6 {
  margin-top: 0;
  margin-bottom: 0;
}

a {
  color: inherit;
}

button,
input,
optgroup,
select,
textarea {
  font-family: inherit;
  outline: none;
}

input {
  border: none;
  background: transparent;
}
</style>

<style lang="scss" scoped>
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  position: relative;
  font-size: rem(15px);

  // disable text-selection
  user-select: none;
}

.app__loading-overlay {
  width: 100%;
  height: 100%;
  z-index: 100;
  position: absolute;
}

.web-gl {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: -1;
}

.view {
  min-height: 100%;
}
</style>
